import { useTranslation } from 'react-i18next';
import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import { GridColDef } from '@mui/x-data-grid';
import { LinearProgress, useMediaQuery, useTheme } from '@mui/material';
import StyledDataGrid from '../../DataGrid/StyledDataGrid';
import { useMemo } from 'react';
import { StyledRouterLink } from '../../StyledLink/StyledLink';
import styled from 'styled-components';
import { formatEuro } from '../../utils/formatters';
import { GridCopyButton } from '../../utils/gridUtils';
import { orderStatusFromTimeStamps } from '../utils/orderStatus';
import { OrderDetailsDrawer } from '../OrderDetails/OrderDetailsDrawer';
import { OrdersFilterBar } from './OrdersFilterBar';
import { OrderStatusChip } from '../OrderStatusChip';
import { OrdersGridLoadingOverlay } from './OrdersListSkeleton';
import { ALL_COLUMNS_VISIBLE, GRID_CELL_PADDING_Y, gridColumn, TABLET_HIDDEN_COLUMNS } from './columnSpecs';
import { OrdersMobileList } from './OrdersMobileList';
import { ALL_SHOPS, DEFAULT_ORDER_FILTER, filterOrders, filterOrdersByShop, sortOrders } from '../utils/orderFilters';
import {
  getCustomerName,
  getDeliveryTypeLabel,
  getOrderPlacedLabel,
  getOrderProductCount,
  OrderRow,
} from '../utils/orderRowFields';
import { useOrderDetailsParam } from '../hooks/useOrderDetailsParam';
import { OrderFilters, useOrderFilters } from '../hooks/useOrderFilters';

export const OrdersList: React.FC<{
  ordersFromQuery: OrderV2[];
  customerOrdersView?: boolean;
  /** Orders are on their way. The list decides for itself how to say so — see below. */
  isLoading?: boolean;
  /**
   * Pass these to place the filter bar yourself — the restaurant view puts it beside the date strip.
   * Omit them and the list keeps its own state and renders the bar above itself.
   */
  filters?: OrderFilters;
}> = ({ ordersFromQuery, customerOrdersView, isLoading = false, filters }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  // Always called — hooks cannot be conditional — but ignored when the page supplies its own.
  const ownFilters = useOrderFilters();
  const { orderFilter, setOrderFilter, sortKey, setSortKey, shopFilter } = filters ?? ownFilters;

  // Picking an order opens it in a panel over this list rather than navigating away, so the list keeps
  // its filters, its scroll position and — on the restaurant page — its selected day.
  const { orderId, openOrder, closeOrder } = useOrderDetailsParam();

  const rows: OrderRow[] = useMemo(
    () => (ordersFromQuery ?? []).map((order) => ({ ...order, orderStatusString: orderStatusFromTimeStamps(order) })),
    [ordersFromQuery]
  );

  const filteredOrders: OrderRow[] = useMemo(
    () => filterOrdersByShop(filterOrders(rows, orderFilter), shopFilter),
    [rows, orderFilter, shopFilter]
  );

  // Only the phone list is sorted here. The grid owns its own order through `initialState.sorting` and
  // its column headers, so handing it pre-sorted rows would give one list two sort authorities — and
  // the sort pill is hidden from sm up precisely to leave the grid's the only one.
  const sortedOrders: OrderRow[] = useMemo(() => sortOrders(filteredOrders, sortKey), [filteredOrders, sortKey]);

  // Empty for two different reasons, and the wording says which. Both layouts read it from here so
  // they cannot drift.
  const isNarrowed = orderFilter !== DEFAULT_ORDER_FILTER || shopFilter !== ALL_SHOPS;
  const emptyMessage = isNarrowed ? t('orderList.noMatches') : t('orderList.noOrders');

  // Loading means two different things and they want opposite treatments: with nothing on screen yet,
  // stand in for the rows; with rows already up, leave them readable and just say a refresh is under
  // way. Measured on `rows` rather than `filteredOrders`, so a filter matching nothing mid-refresh
  // cannot flip the list back to placeholders.
  const isInitialLoad = isLoading && rows.length === 0;
  const isRefreshing = isLoading && rows.length > 0;

  const columns: GridColDef[] = [
    {
      ...gridColumn('id'),
      headerName: t('Order ID'),
      renderCell: (params) => (
        <>
          <GridCopyButton params={params} />
          {/* The row click opens the panel, so the link has to keep its own click: without this,
              following it to the full page would open the panel on the way out. */}
          <span onClick={(event) => event.stopPropagation()}>
            <StyledRouterLink to={`/orders/${params.value}`}> {params.value}</StyledRouterLink>
          </span>
        </>
      ),
    },
    {
      ...gridColumn('deliveryContact'),
      headerName: t('Customer'),
      renderCell: (params) => {
        return (
          <div>
            <div>{getCustomerName(params.row)}</div>
            {params.value?.email && <div> {params.value.email}</div>}
          </div>
        );
      },
    },
    {
      ...gridColumn('orderPlaced'),
      headerName: t('Order placed'),
      renderCell: (params) => {
        const placed = getOrderPlacedLabel(params.row, t, customerOrdersView);

        return (
          <div>
            {placed.isPreorder ? <PreorderText>{placed.text}</PreorderText> : <PlacedText>{placed.text}</PlacedText>}
          </div>
        );
      },
    },
    {
      ...gridColumn('deliveryType'),
      headerName: t('Delivery method'),
      renderCell: (params) => getDeliveryTypeLabel(params.row, t),
    },
    {
      ...gridColumn('orderStatusString'),
      headerName: t('Status'),
      renderCell: (params) => <OrderStatusChip status={params.value} />,
    },
    {
      ...gridColumn('shoppingCartPrice'),
      headerName: t('Products'),
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => getOrderProductCount(params.row),
    },
    {
      ...gridColumn('grandTotal'),
      headerName: t('global.price'),
      valueGetter: (params) => params.row.shoppingCartPrice?.grandTotal,
      renderCell: (params) => formatEuro(params.value),
    },
  ];

  return (
    <Container aria-busy={isLoading}>
      {!filters && (
        <OrdersFilterBar
          orderFilter={orderFilter}
          onOrderFilterChange={setOrderFilter}
          sortKey={sortKey}
          onSortKeyChange={setSortKey}
        />
      )}
      {/* Always rendered, so starting a refresh does not nudge the list down by the bar's height. */}
      <ProgressSlot>{isRefreshing && <LinearProgress aria-label={t('global.loading')} />}</ProgressSlot>
      <ListArea $dimmed={isRefreshing}>
        {isPhone ? (
          <OrdersMobileList
            orders={sortedOrders}
            customerOrdersView={customerOrdersView}
            emptyMessage={emptyMessage}
            isLoading={isInitialLoad}
            onOrderSelect={openOrder}
          />
        ) : (
          <OrdersGridContainer>
            <StyledDataGrid
              getRowHeight={() => 'auto'}
              // Row selection stays off: its `Mui-selected` rule paints the whole row solid primary,
              // far too loud for marking which order the panel happens to be showing.
              disableRowSelectionOnClick
              onRowClick={(params) => openOrder(String(params.id))}
              // The keyboard equivalent of that row click. No column is editable, so Enter is free; the
              // grid otherwise swallows it.
              onCellKeyDown={(params, event) => {
                if (event.key === 'Enter') {
                  event.defaultMuiPrevented = true;
                  openOrder(String(params.id));
                }
              }}
              getRowClassName={(params) => (params.id === orderId ? 'grid-row-open' : '')}
              rows={filteredOrders}
              localeText={{ noRowsLabel: emptyMessage }}
              columns={columns}
              columnVisibilityModel={isTablet ? TABLET_HIDDEN_COLUMNS : ALL_COLUMNS_VISIBLE}
              getRowId={(row) => row.id}
              // The grid's own overlay, which wins over the empty-state text while it shows, so the
              // two can never appear at once.
              loading={isInitialLoad}
              slots={{ loadingOverlay: OrdersGridLoadingOverlay }}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'orderPlaced', sort: 'desc' }],
                },
              }}
            />
          </OrdersGridContainer>
        )}
      </ListArea>
      <OrderDetailsDrawer orderId={orderId} onClose={closeOrder} />
    </Container>
  );
};

// Was a <Typography variant="h2">, which put the whole table inside a heading and made every plain
// element inside it inherit 21px/600.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
`;

const PlacedText = styled.div`
  padding: 4px 8px;
`;

/** The yellow preorder highlight. */
const PreorderText = styled(PlacedText)`
  background: ${({ theme }) => theme.palette.highlight.main};
  font-weight: bold;
`;

// Reserved whether or not a refresh is running, so the bar appearing costs no layout shift.
const ProgressSlot = styled.div`
  height: 4px;
  margin-bottom: 6px;
`;

// Dimmed rather than replaced while refreshing: the rows underneath are still the last true answer,
// and still worth clicking — opening an order by id does not depend on the list being current.
const ListArea = styled.div<{ $dimmed: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.55 : 1)};
  transition: opacity 150ms ease;
`;

// The phone branch is intentionally not wrapped in this: only the grid needs an explicit height.
const OrdersGridContainer = styled.div`
  display: flex;
  width: 100%;
  height: 70vh;
  min-height: 320px;

  /* Rows are measured from their tallest cell, and MUI gives a cell no vertical padding at all — so a
     row was exactly as tall as the status chip in it, with the customer's two lines touching the
     dividers above and below. The same measure columnSpecs gives the placeholder rows. */
  .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell {
    padding-top: ${GRID_CELL_PADDING_Y}px;
    padding-bottom: ${GRID_CELL_PADDING_Y}px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    height: calc(100vh - 300px);
  }
`;
